import { useState, useEffect } from "react";
import { FileText, Plus, Search, Eye, Edit2, Trash2, FileCheck, X, Package, Calendar, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-100 text-blue-700",
  invoiced: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700"
};

export default function SalesOrderManagement() {
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);

  const [form, setForm] = useState({
    customer_id: "", delivery_date: "", delivery_address: "", notes: "", gst_type: "cgst_sgst",
    items: [{ product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }]
  });

  const canEdit = ["owner", "manager"].includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, productsRes] = await Promise.all([
        fetch(`${API}/api/sales-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchOrderDetail = async (orderId) => {
    try {
      const res = await fetch(`${API}/api/sales-orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setOrderDetail(await res.json()); setShowDetailModal(true); }
    } catch (e) { toast.error("Failed to load order details"); }
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }] });
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].unit_price = product.price?.domestic || 0;
      }
    }
    setForm({ ...form, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const totalGst = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.gst_percent / 100), 0);
    return { subtotal, totalGst, grandTotal: subtotal + totalGst };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Please select a customer"); return; }
    if (form.items.some(item => !item.product_name || item.quantity <= 0)) { toast.error("Please fill all item details"); return; }

    const method = selectedOrder ? "PUT" : "POST";
    const url = selectedOrder ? `${API}/api/sales-orders/${selectedOrder.id}` : `${API}/api/sales-orders`;
    try {
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(selectedOrder ? "Order updated" : "Order created");
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save order");
      }
    } catch (e) { toast.error("Failed to save order"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      const res = await fetch(`${API}/api/sales-orders/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Order deleted"); fetchData(); }
      else {
        const err = await res.json();
        toast.error(err.detail || "Cannot delete order");
      }
    } catch (e) { toast.error("Failed to delete order"); }
  };

  const handleCreateInvoice = async (orderId) => {
    if (!window.confirm("Create invoice from this order?")) return;
    try {
      const res = await fetch(`${API}/api/invoices/from-order/${orderId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Invoice created successfully");
        fetchData();
        setShowDetailModal(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create invoice");
      }
    } catch (e) { toast.error("Failed to create invoice"); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API}/api/sales-orders/${orderId}`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (res.ok) { toast.success("Status updated"); fetchData(); setShowDetailModal(false); }
    } catch (e) { toast.error("Failed to update status"); }
  };

  const resetForm = () => {
    setSelectedOrder(null);
    setForm({
      customer_id: "", delivery_date: "", delivery_address: "", notes: "", gst_type: "cgst_sgst",
      items: [{ product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }]
    });
  };

  const filtered = orders.filter(o => {
    const matchesSearch = o.order_number.toLowerCase().includes(search.toLowerCase()) ||
      o.customer_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const { subtotal, totalGst, grandTotal } = calculateTotals();

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-forest/20 border-t-forest rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sales-order-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-navy">Sales Orders</h1>
          <p className="text-slate-500 text-sm font-sans">Create and manage sales orders</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-forest hover:bg-forest/90" data-testid="add-order-btn">
          <Plus className="w-4 h-4 mr-2" /> New Order
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by order # or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-orders" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(order => (
              <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                <TableCell className="font-medium">{order.order_number}</TableCell>
                <TableCell>{order.customer_name}</TableCell>
                <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right font-medium">₹{order.grand_total?.toLocaleString()}</TableCell>
                <TableCell><Badge className={statusColors[order.status]}>{order.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => fetchOrderDetail(order.id)} data-testid={`view-order-${order.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canEdit && order.status === "draft" && (
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(order.id)} data-testid={`delete-order-${order.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            {search || statusFilter !== "all" ? "No orders match your filters" : "No sales orders yet"}
          </div>
        )}
      </Card>

      {/* Create/Edit Order Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOrder ? "Edit Order" : "New Sales Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={v => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Delivery Date</Label>
                <Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} data-testid="input-delivery-date" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Delivery Address</Label>
                <Textarea value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} rows={2} data-testid="input-delivery-address" />
              </div>
              <div>
                <Label>GST Type</Label>
                <Select value={form.gst_type} onValueChange={v => setForm({ ...form, gst_type: v })}>
                  <SelectTrigger data-testid="select-gst-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cgst_sgst">CGST + SGST (Intra-state)</SelectItem>
                    <SelectItem value="igst">IGST (Inter-state)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {form.items.map((item, index) => (
                  <div key={index} className="grid gap-2 sm:grid-cols-6 p-3 bg-slate-50 rounded-lg items-end">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Product</Label>
                      <Select value={item.product_id} onValueChange={v => updateItem(index, "product_id", v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min="0.1" step="0.1" className="h-9" value={item.quantity} onChange={e => updateItem(index, "quantity", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Select value={item.unit} onValueChange={v => updateItem(index, "unit", v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="tons">tons</SelectItem>
                          <SelectItem value="blocks">blocks</SelectItem>
                          <SelectItem value="rolls">rolls</SelectItem>
                          <SelectItem value="pieces">pieces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Price (₹)</Label>
                      <Input type="number" min="0" className="h-9" value={item.unit_price} onChange={e => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">GST %</Label>
                        <Input type="number" min="0" max="28" className="h-9" value={item.gst_percent} onChange={e => updateItem(index, "gst_percent", parseFloat(e.target.value) || 0)} />
                      </div>
                      {form.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" className="text-red-500 h-9" onClick={() => removeItem(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">₹{subtotal.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>GST:</span><span className="font-medium">₹{totalGst.toLocaleString()}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span>₹{grandTotal.toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="input-notes" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-order">{selectedOrder ? "Update" : "Create"} Order</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {orderDetail && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-navy">{orderDetail.order_number}</h3>
                  <p className="text-slate-500">{orderDetail.customer_name}</p>
                </div>
                <Badge className={statusColors[orderDetail.status]}>{orderDetail.status}</Badge>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 text-sm">
                <div><span className="text-slate-500">Created:</span> {new Date(orderDetail.created_at).toLocaleDateString()}</div>
                {orderDetail.delivery_date && <div><span className="text-slate-500">Delivery:</span> {orderDetail.delivery_date}</div>}
                {orderDetail.delivery_address && <div className="sm:col-span-2"><span className="text-slate-500">Address:</span> {orderDetail.delivery_address}</div>}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderDetail.items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                      <TableCell className="text-right">₹{item.unit_price}</TableCell>
                      <TableCell className="text-right">{item.gst_percent}%</TableCell>
                      <TableCell className="text-right font-medium">₹{(item.quantity * item.unit_price).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal:</span><span>₹{orderDetail.subtotal?.toLocaleString()}</span></div>
                  {orderDetail.gst_type === "cgst_sgst" ? (
                    <>
                      <div className="flex justify-between"><span>CGST:</span><span>₹{orderDetail.cgst?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>SGST:</span><span>₹{orderDetail.sgst?.toLocaleString()}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span>IGST:</span><span>₹{orderDetail.igst?.toLocaleString()}</span></div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total:</span><span>₹{orderDetail.grand_total?.toLocaleString()}</span></div>
                </div>
              </div>

              {orderDetail.notes && <div className="text-sm p-3 bg-slate-50 rounded"><span className="text-slate-500">Notes:</span> {orderDetail.notes}</div>}

              {canEdit && (
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {orderDetail.status === "draft" && (
                    <Button size="sm" onClick={() => updateOrderStatus(orderDetail.id, "confirmed")} data-testid="confirm-order-btn">
                      <FileCheck className="w-4 h-4 mr-1" /> Confirm Order
                    </Button>
                  )}
                  {["draft", "confirmed"].includes(orderDetail.status) && (
                    <Button size="sm" variant="outline" onClick={() => handleCreateInvoice(orderDetail.id)} data-testid="create-invoice-btn">
                      <FileText className="w-4 h-4 mr-1" /> Create Invoice
                    </Button>
                  )}
                  {orderDetail.status === "draft" && (
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => updateOrderStatus(orderDetail.id, "cancelled")}>
                      Cancel Order
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
